import { defineAsyncComponent, reactive, unref } from 'vue'
import type { I18n } from 'vue-i18n'
import { ENDPOINTS } from '@/lib/consts.ts'
import { useCore } from '@/composables/core'
import {
  initialize,
  IterableEmbeddedManager,
  IterableEmbeddedSessionManager,
  trackEmbeddedClick,
  updateUser,
} from '@iterable/web-sdk'
import { PAGE_SOURCES } from '@/lib/analytics/consts.ts'
import { notificationAction } from '@/lib/notifications'
import { PROMO_BANNER_THEMES } from '@/components/top-bar/consts.ts'

const APP_PACKAGE_NAME = 'booksy.com'

type IterableRoutingState = {
  routingConfig: {
    config: {
      iterable_api_key: string
      embedded_messages_placement_ids?: Record<string, string | number | null | undefined>
    }
  }
}

type IterableSdkInstance = {
  setEmail: (email: string) => Promise<string | void>
  logout: () => void
}

type IterableState = {
  instance?: IterableSdkInstance
  embeddedManager?: IterableEmbeddedManager
  embeddedSessionManager?: IterableEmbeddedSessionManager
}

const _iterable = reactive<IterableState>({})

export const createIterableUserInstance = async (): Promise<void> => {
  if (!_iterable.instance) {
    const { apiCall, store, i18n } = useCore()

    const fetchJwtForIterable = async (): Promise<string> => {
      const res = (await apiCall({
        url: ENDPOINTS.ITERABLE_JWT,
      })) as { data?: { token?: string } }
      return res.data?.token ?? ''
    }

    _iterable.embeddedSessionManager = new IterableEmbeddedSessionManager(APP_PACKAGE_NAME)
    _iterable.embeddedManager = new IterableEmbeddedManager(APP_PACKAGE_NAME)

    _iterable.instance = initialize(
      (store.state as IterableRoutingState).routingConfig.config.iterable_api_key,
      fetchJwtForIterable
    ) as unknown as IterableSdkInstance

    _iterable.embeddedManager.addUpdateListener({
      onMessagesUpdated: () => {
        /* It is only executed while syncMessages is called.  */
      },
      onEmbeddedMessagingDisabled: () => {
      },
    })

    const email = (store.state as { identity: { user: { email: string } } }).identity.user.email
    await _iterable.instance.setEmail(email)

    const locale = unref((i18n as I18n).global.locale)

    await updateUser({
      dataFields: {
        locale: String(locale),
      },
    })

    _iterable.embeddedManager.syncMessages(
      APP_PACKAGE_NAME,
      () => {}
    )
  } else {
    throw new Error('Iterable instance already created, destroy it first before create new one.')
  }
}

export const destroyIterableUserInstance = (): void => {
  if (_iterable.instance) {
    _iterable.instance.logout()
    _iterable.instance = undefined
    _iterable.embeddedManager = undefined
    _iterable.embeddedSessionManager = undefined
  }
}

type IterableBannerOptionalParams = {
  date?: string | number | Date
}

export const useIterableBanners = (): {
  getBannerForPlacement: (pageSource: string, optionalParams?: IterableBannerOptionalParams) => unknown
  startSession: () => void
  endSession: () => void
} => {
  if (_iterable.instance && _iterable.embeddedManager && _iterable.embeddedSessionManager) {
    const embeddedManager = _iterable.embeddedManager
    const embeddedSessionManager = _iterable.embeddedSessionManager

    return {
      getBannerForPlacement: (pageSource, optionalParams = {}) => {
        const _renderIterableBanner = (placementId: string | number | null): unknown => {
          if (placementId !== null && placementId !== '') {
            const [message] = embeddedManager.getMessagesForPlacement(Number(placementId))

            if (message) {
              const { title, body, buttons } = message.elements
              const { theme = PROMO_BANNER_THEMES.GRADIENT, icon } = message.payload ?? {}

              if (!title || !buttons?.[0]?.title) return

              return {
                component: defineAsyncComponent(() => import('@/components/top-bar/promo-banner.vue')),
                options: {
                  titleText: title,
                  description: body,
                  btnTitle: buttons[0].title,
                  theme,
                  icon,
                  ctaAction: () => {
                    if (buttons[0].id && optionalParams.date !== undefined) {
                      updateUser({
                        mergeNestedObjects: true,
                        dataFields: {
                          embeddedMessagesButtonsClickDates: {
                            [buttons[0].id]: optionalParams.date,
                          },
                        },
                      })
                    }

                    void trackEmbeddedClick({
                      messageId: message.metadata.messageId,
                      buttonIdentifier: buttons[0].id,
                      appPackageName: APP_PACKAGE_NAME,
                    } as Parameters<typeof trackEmbeddedClick>[0])

                    notificationAction({
                      target_type: buttons[0].action.type,
                      target_id: undefined,
                      params: undefined,
                    } as Parameters<typeof notificationAction>[0])
                  },
                  onMounted: () => {
                    embeddedSessionManager.startImpression(message.metadata.messageId, Number(placementId))
                  },
                  onUnmounted: () => {
                    embeddedSessionManager.pauseImpression(message.metadata.messageId)
                  },
                },
              }
            }
          }
        }

        const _placementsStrategy: Record<string, () => unknown> = {
          [PAGE_SOURCES.PAYMENTS_AND_CHECKOUT]: () => {
            const state = useCore().store.state as IterableRoutingState
            const placementId = state.routingConfig.config?.embedded_messages_placement_ids?.PAYMENTS_AND_CHECKOUT ?? null
            return _renderIterableBanner(placementId)
          },
          [PAGE_SOURCES.CALENDAR]: () => {
            const state = useCore().store.state as IterableRoutingState
            const placementId = state.routingConfig.config?.embedded_messages_placement_ids?.CALENDAR ?? null
            return _renderIterableBanner(placementId)
          },
        }
        return _placementsStrategy[pageSource]?.()
      },
      startSession: () => embeddedSessionManager.startSession(),
      endSession: () => embeddedSessionManager.endSession(),
    }
  }

  throw new Error('Iterable instance not created.')
}
